import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Droplet, Beaker, Calculator, ChevronDown, ChevronUp, Play } from "lucide-react";
import { useWeight } from "@/contexts/WeightContext";
import { logInterventionWithToast } from "@/utils/interventionLogger";

/**
 * Convert pump rate to mL/hr given:
 *  - dose:       numeric value the medic dialed in
 *  - dose_unit:  e.g. "mcg/kg/min", "mg/kg/hr", "mcg/min"
 *  - weight_kg:  patient weight (ignored if dose_unit has no /kg)
 *  - conc:       drug concentration value (e.g. 16)
 *  - conc_unit:  drug concentration unit (e.g. "mcg")  — assumes per-mL
 *
 * Returns mL/hr, or null if not enough info.
 */
function calcRateMlHr({ dose, dose_unit, weight_kg, conc, conc_unit }) {
  if (!dose || !conc || dose <= 0 || conc <= 0) return null;
  const u = (dose_unit || "").toLowerCase();
  const cu = (conc_unit || "mcg").toLowerCase();

  // Total drug per minute, expressed in the same unit as concentration.
  // Dose unit format: "<mass>/[kg/]<time>" e.g. "mcg/kg/min", "mg/kg/hr", "mcg/min"
  const massUnit = u.split("/")[0];   // mcg | mg
  const perKg = u.includes("/kg/");
  const perTime = u.endsWith("/min") ? "min" : u.endsWith("/hr") ? "hr" : null;
  if (!perTime) return null;

  // Dose per minute in original mass unit
  let massPerMin = parseFloat(dose);
  if (perKg) {
    if (!weight_kg || weight_kg <= 0) return null;
    massPerMin *= parseFloat(weight_kg);
  }
  if (perTime === "hr") massPerMin /= 60;

  // Convert dose mass to concentration mass unit
  if (massUnit !== cu) {
    if (massUnit === "mg" && cu === "mcg") massPerMin *= 1000;
    else if (massUnit === "mcg" && cu === "mg") massPerMin /= 1000;
    else return null;
  }

  // mL/min = massPerMin / conc(mass/mL) ; mL/hr = mL/min * 60
  return (massPerMin / parseFloat(conc)) * 60;
}

const RecipeBlock = ({ option }) => {
  if (!option || !option.recipe) return null;
  const r = option.recipe;
  if (option.type === "infusion") {
    return (
      <div className="text-xs space-y-1">
        <div className="font-semibold text-cyan-400">Mix recipe — IV bag</div>
        <div className="font-mono">
          {r.dose_amount} {r.dose_unit} of drug → into {r.bag_volume_ml} mL bag of {r.diluent || "diluent"}
        </div>
        <div className="font-mono text-emerald-400">
          = {option.conc} {option.unit}/mL final concentration
        </div>
      </div>
    );
  }
  if (option.type === "dilution") {
    return (
      <div className="text-xs space-y-1">
        <div className="font-semibold text-amber-400">Mix recipe — syringe</div>
        <div className="font-mono">
          Draw {r.draw_ml} mL of {r.stock_conc} {r.stock_unit}/mL stock → add {r.diluent || "NS"} to {r.total_ml} mL total
        </div>
        <div className="font-mono text-emerald-400">
          = {option.conc} {option.unit}/mL final concentration
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Infusion Rate Calculator card for IV drips.
 *
 * Props:
 *  - drug:    string — drug name (e.g. "norepinephrine")
 *  - options: array of concentration options (from drug-concentrations API)
 *  - dosing:  { min_dose, max_dose, typical_start, dose_unit } (infusion_dosing)
 */
export default function InfusionRateCalculator({ drug, options, dosing }) {
  const { weight, hasWeight } = useWeight();
  const [showRecipe, setShowRecipe] = useState(false);
  // Persist expand/collapse choice per-drug so the medic's preference sticks
  const storageKey = `yems_infusion_open_${drug}`;
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, expanded ? "1" : "0"); } catch {}
  }, [expanded, storageKey]);
  const drippableOptions = useMemo(
    () => (options || []).filter((o) => o.type === "infusion" || o.type === "stock"),
    [options]
  );
  const defaultIdx = useMemo(() => {
    const i = drippableOptions.findIndex((o) => o.type === "infusion" && o.default);
    if (i >= 0) return i;
    const j = drippableOptions.findIndex((o) => o.default);
    return j >= 0 ? j : 0;
  }, [drippableOptions]);
  const [optIdx, setOptIdx] = useState(defaultIdx);
  useEffect(() => { setOptIdx(defaultIdx); }, [defaultIdx]);

  const [dose, setDose] = useState(dosing?.typical_start ?? 0);
  useEffect(() => { setDose(dosing?.typical_start ?? 0); }, [dosing]);

  const selected = drippableOptions[optIdx];
  const weightKg = hasWeight ? parseFloat(weight) : null;
  const ratePerKgMin = dosing?.dose_unit?.includes("/kg/") ? dose : null;
  const totalDoseMin = useMemo(() => {
    if (!dose || !dosing) return null;
    const u = dosing.dose_unit.toLowerCase();
    const perKg = u.includes("/kg/");
    const perTime = u.endsWith("/min") ? "min" : "hr";
    let total = parseFloat(dose);
    if (perKg) {
      if (!weightKg) return null;
      total *= weightKg;
    }
    if (perTime === "hr") total /= 60;
    return total; // in dose_unit's mass per minute
  }, [dose, dosing, weightKg]);

  const mlHr = useMemo(() => {
    if (!selected || !dosing) return null;
    return calcRateMlHr({
      dose,
      dose_unit: dosing.dose_unit,
      weight_kg: weightKg,
      conc: selected.conc,
      conc_unit: selected.unit,
    });
  }, [selected, dosing, dose, weightKg]);

  if (!selected || !dosing) return null;

  const massUnit = dosing.dose_unit.split("/")[0];
  const drugTitle = drug.charAt(0).toUpperCase() + drug.slice(1);
  const isWeightBased = (dosing.dose_unit || "").includes("/kg/");

  // Collapsed compact view — saves screen real estate when not in use
  if (!expanded) {
    return (
      <Card className="border-purple-500/30" data-testid={`infusion-calc-${drug}`}>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-purple-500/5 transition-colors"
          data-testid={`infusion-expand-${drug}`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Droplet className="w-4 h-4 text-purple-400 shrink-0" />
            <span className="font-heading font-semibold text-sm truncate">{drugTitle} Infusion Rate</span>
            <Badge variant="outline" className="text-[9px] font-normal shrink-0">{dosing.dose_unit}</Badge>
            {!isWeightBased && (
              <Badge variant="outline" className="text-[9px] font-normal text-emerald-400 border-emerald-500/40 shrink-0">Fixed dose</Badge>
            )}
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-muted-foreground hidden sm:inline">Tap to expand</span>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </span>
        </button>
      </Card>
    );
  }

  return (
    <Card className="border-purple-500/30" data-testid={`infusion-calc-${drug}`}>
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between gap-2 text-base font-heading">
          <span className="flex items-center gap-2 min-w-0">
            <Droplet className="w-4 h-4 text-purple-400" />
            <span className="truncate">{drugTitle} Infusion Rate</span>
            <Badge variant="outline" className="text-[9px] font-normal" title="Infusion">Inf</Badge>
            <Badge variant="outline" className="text-[9px] font-normal">{dosing.dose_unit}</Badge>
            {!isWeightBased && (
              <Badge variant="outline" className="text-[9px] font-normal text-emerald-400 border-emerald-500/40">Fixed dose</Badge>
            )}
          </span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="shrink-0 p-1 rounded hover:bg-secondary/40 text-muted-foreground"
            data-testid={`infusion-collapse-${drug}`}
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {/* Concentration selector */}
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bag concentration</div>
          <div className="flex flex-wrap gap-1.5">
            {drippableOptions.map((opt, i) => (
              <button
                key={i}
                onClick={() => setOptIdx(i)}
                className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all ${optIdx === i ? "bg-purple-600 text-white ring-2 ring-purple-500/50" : "bg-secondary/40 text-muted-foreground hover:bg-secondary/60"}`}
                data-testid={`conc-opt-${drug}-${i}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dose — direct entry */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dose</span>
            <span className="text-[10px] text-muted-foreground">
              range {dosing.min_dose}–{dosing.max_dose} {dosing.dose_unit}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const step = dosing.min_dose < 1 ? 0.01 : 0.5;
                const next = Math.max(0, parseFloat((parseFloat(dose) - step).toFixed(2)));
                setDose(next);
              }}
              className="h-10 w-10 rounded-lg bg-secondary/40 hover:bg-secondary/60 text-lg font-bold text-muted-foreground transition-all"
              data-testid={`dose-decrement-${drug}`}
            >−</button>
            <div className="flex-1 relative">
              <input
                type="number"
                inputMode="decimal"
                step={dosing.min_dose < 1 ? 0.01 : 0.5}
                min={0}
                value={dose}
                onChange={(e) => setDose(e.target.value === "" ? 0 : parseFloat(e.target.value))}
                onFocus={(e) => e.target.select()}
                className={`w-full h-10 rounded-lg border-2 bg-secondary/20 text-center font-mono font-bold text-xl tabular-nums focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                  dose > dosing.max_dose || dose < dosing.min_dose
                    ? "border-amber-500/60 text-amber-400"
                    : "border-purple-500/40 text-purple-300"
                }`}
                data-testid={`dose-input-${drug}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none font-mono">
                {dosing.dose_unit}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                const step = dosing.min_dose < 1 ? 0.01 : 0.5;
                const next = parseFloat((parseFloat(dose) + step).toFixed(2));
                setDose(next);
              }}
              className="h-10 w-10 rounded-lg bg-secondary/40 hover:bg-secondary/60 text-lg font-bold text-muted-foreground transition-all"
              data-testid={`dose-increment-${drug}`}
            >+</button>
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setDose(dosing.min_dose)}
              className="flex-1 h-7 rounded text-[10px] font-medium bg-secondary/30 hover:bg-secondary/50 text-muted-foreground"
              data-testid={`dose-preset-min-${drug}`}
            >
              Min · {dosing.min_dose}
            </button>
            <button
              type="button"
              onClick={() => setDose(dosing.typical_start)}
              className="flex-1 h-7 rounded text-[10px] font-bold bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/30"
              data-testid={`dose-preset-typical-${drug}`}
            >
              Typical · {dosing.typical_start}
            </button>
            <button
              type="button"
              onClick={() => setDose(dosing.max_dose)}
              className="flex-1 h-7 rounded text-[10px] font-medium bg-secondary/30 hover:bg-secondary/50 text-muted-foreground"
              data-testid={`dose-preset-max-${drug}`}
            >
              Max · {dosing.max_dose}
            </button>
          </div>
          {(dose > dosing.max_dose || (dose > 0 && dose < dosing.min_dose)) && (
            <div className="text-[10px] text-amber-400 text-center font-medium">
              ⚠ Dose is outside typical range ({dosing.min_dose}–{dosing.max_dose} {dosing.dose_unit})
            </div>
          )}
        </div>

        {/* Output */}
        <div className={`grid gap-2 ${isWeightBased ? "grid-cols-3" : "grid-cols-2"}`}>
          <div className="bg-secondary/30 border border-border rounded-lg p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Pump rate</div>
            <div className="text-2xl font-mono font-bold text-emerald-400">
              {mlHr !== null && !Number.isNaN(mlHr) ? mlHr.toFixed(1) : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground">mL/hr</div>
          </div>
          <div className="bg-secondary/30 border border-border rounded-lg p-2 text-center">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Total dose</div>
            <div className="text-lg font-mono font-bold text-purple-300">
              {totalDoseMin !== null ? totalDoseMin.toFixed(2) : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground">{massUnit}/min</div>
          </div>
          {isWeightBased && (
            <div className="bg-secondary/30 border border-border rounded-lg p-2 text-center">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Weight</div>
              <div className="text-lg font-mono font-bold text-foreground">
                {hasWeight ? weight : "—"}
              </div>
              <div className="text-[10px] text-muted-foreground">kg {hasWeight ? "" : "(set in header)"}</div>
            </div>
          )}
        </div>

        {!hasWeight && dosing.dose_unit.includes("/kg/") && (
          <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-2 text-center">
            Enter patient weight to calculate the pump rate
          </div>
        )}

        {/* Start Infusion — log to intervention tracker */}
        <Button
          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          disabled={mlHr === null || Number.isNaN(mlHr)}
          onClick={() => {
            const dosePart = `${parseFloat(dose).toFixed(dosing.min_dose < 1 ? 2 : 1)} ${dosing.dose_unit}`;
            const ratePart = mlHr !== null ? `${mlHr.toFixed(1)} mL/hr` : "—";
            const bagPart = selected.label;
            logInterventionWithToast(
              {
                type: "infusion",
                name: `${drugTitle} infusion`,
                dose: dosePart,
                route: "IV",
                notes: `Bag: ${bagPart} · Rate: ${ratePart}${hasWeight ? ` · Wt ${weight} kg` : ""}`,
              },
              `${drugTitle} infusion started — ${ratePart}`
            );
          }}
          data-testid={`start-infusion-${drug}`}
        >
          <Play className="w-4 h-4 mr-2" />
          Start Infusion &amp; Log
        </Button>

        {/* Recipe collapsible */}
        {selected.recipe && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs justify-between"
              onClick={() => setShowRecipe((v) => !v)}
              data-testid={`recipe-toggle-${drug}`}
            >
              <span className="flex items-center gap-1.5">
                <Beaker className="w-3.5 h-3.5" />
                Mix recipe
              </span>
              {showRecipe ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
            {showRecipe && (
              <div className="mt-1 p-2.5 bg-secondary/20 border border-border rounded-lg" data-testid={`recipe-${drug}`}>
                <RecipeBlock option={selected} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { calcRateMlHr };
